import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from '../../services/api.service';
import { isPlatformBrowser } from '@angular/common';
import { Inject, PLATFORM_ID } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, delay, debounceTime } from 'rxjs/operators';

interface ContentItem {
  title: string;
  header: string;
  content: string;
  slug: string;
  data?: any;
  titles?: BoardTitle[];
}

interface BoardTitle {
  title: string;
  editors: any[];
}

interface AboutGroup {
  title: string;
  slug: string;
  items: ContentItem[];
}

@Component({
    selector: 'app-about',
    templateUrl: './about.component.html',
    styleUrls: ['./about.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AboutComponent implements OnInit, OnDestroy {
  isLoading = false;
  error: string | null = null;
  items: AboutGroup[] = [];
  journal: any = null;
  currentSlug: string = 'about';
  activeGroup: AboutGroup | null = null;
  activeAccordion = -1;
  activeTab = -1;

  private destroy$ = new Subject<void>();
  private isInitialLoad = true;
  private isLoadingData = false;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private translateService: TranslateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    // Dil değişikliğini dinle
    this.translateService.onLangChange.pipe(
      takeUntil(this.destroy$),
      debounceTime(200)
    ).subscribe((event) => {
      if (!this.isInitialLoad) {
        this.apiService.setCurrentLang(event.lang);
        this.loadData();
      }
    });

    // Route parametrelerini dinle
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.currentSlug = params['slug'] || 'about';

      // Sadece ilk yüklemede veri çek
      if (this.isInitialLoad) {
        this.loadData();
        this.isInitialLoad = false;
      } else {
        // Sadece aktif grubu güncelle
        this.setActiveGroup();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadData(): Promise<void> {
    // Eğer zaten yükleme yapılıyorsa, tekrar çağırma
    if (this.isLoadingData) {
      return;
    }

    try {
      this.isLoadingData = true;
      this.isLoading = true;
      this.error = null;

      const [journal, contents] = await Promise.all([
        this.apiService.getJournalBySlug('home').toPromise(),
        this.apiService.getJournalContentsBySlug('home').toPromise()
      ]);

      this.journal = journal;
      this.items = contents || [];

      // Set active group based on current slug
      this.setActiveGroup();

    } catch (error) {
      console.error('Error loading about data:', error);
      this.error = 'Sayfa yüklenirken bir hata oluştu.';
    } finally {
      this.isLoading = false;
      this.isLoadingData = false;
    }
  }

  private setActiveGroup(): void {
    this.activeGroup = this.items.find(group => group.slug === this.currentSlug) || null;

    if (this.activeGroup) {
      const groupIndex = this.items.findIndex(group => group.slug === this.currentSlug);
      this.activeAccordion = groupIndex;
      
      // Her zaman ilk tab'ı aktif yap (tek item varsa 0, çoklu item varsa -1)
      const hasMultiContent = this.activeGroup.items.length > 1;
      this.activeTab = hasMultiContent ? -1 : 0;
    }
  }

  setAccordionState(groupIndex: number, itemIndex: number): void {
  const group = this.items[groupIndex];
  
  if (itemIndex >= 0 && this.activeAccordion === groupIndex && this.activeTab === itemIndex) {
    this.activeTab = -1; // Close if already open
    return;
  }
  
  this.activeAccordion = groupIndex;
  this.activeTab = itemIndex;
}

  navigateToGroup(groupSlug: string, groupIndex: number): void {
  const group = this.items[groupIndex];
  const hasMultiContent = group.items.length > 1;
  
  this.activeAccordion = groupIndex;
  this.activeTab = hasMultiContent ? -1 : 0;
  
  // Only navigate if the slug is different from current slug
  if (this.currentSlug !== groupSlug) {
    this.router.navigate(['/about', groupSlug]);
  }
}

  isGroupActive(groupSlug: string): boolean {
    return this.currentSlug === groupSlug;
  }

  isAccordionItemActive(groupIndex: number, itemIndex: number): boolean {
    return this.activeAccordion === groupIndex && this.activeTab === itemIndex;
  }

getTranslatedText(text: any, forceTranslate: boolean = false): string {
  if (!text) return '';

  if (typeof text === 'object') {
    const currentLang = this.apiService.getCurrentLang();
    if (text[`@${currentLang}`]) {
      return text[`@${currentLang}`];
    }
    if (text['@tr']) {
      return text['@tr'];
    }
    if (text['@en']) {
      return text['@en'];
    }
    return text.toString();
  }

  if (typeof text === 'string') {
    if (text.startsWith('@')) {
      // Önce @ işareti ile birlikte dene
      let translated = this.translateService.instant(text);
      if (translated && translated !== text) {
        return translated;
      }

      // @ işareti olmadan dene
      const key = text.substring(1);
      translated = this.translateService.instant(key);
      return (translated && translated !== key) ? translated : text;
    }
    return text;
  }

  return text?.toString() || '';
}

  formatContent(content: string): string {
    if (!content) return '';
    return content.split('\n').join('<br />');
  }

  shareOnTwitter(): void {
    if (isPlatformBrowser(this.platformId)) {
      const url = encodeURIComponent(window.location.href);
      const text = encodeURIComponent('DergiPark - About');
      window.open(`https://twitter.com/intent/tweet?url=${url}&text=${text}`, '_blank');
    }
  }

  shareOnWhatsApp(): void {
    if (isPlatformBrowser(this.platformId)) {
      const url = encodeURIComponent(window.location.href);
      const text = encodeURIComponent('DergiPark - About');
      window.open(`https://api.whatsapp.com/send?text=${text} ${url}`, '_blank');
    }
  }

  shareOnLinkedIn(): void {
    if (isPlatformBrowser(this.platformId)) {
      const url = encodeURIComponent(window.location.href);
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
    }
  }

  copyUrlToClipboard(): void {
    if (isPlatformBrowser(this.platformId)) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        // Could show a toast notification here
      });
    }
  }
}